import { DataProxy, FetchResult } from '@apollo/react-hooks';
import { StorageType, Store } from '@nakedplatform/support';

import { RemoveProspectFromBasket } from 'schema/@types/public/RemoveProspectFromBasket';
import { GuestBasketListViewFragment } from 'schema/public/definitions/basket/fragments.graphql';
import { GuestBasketListViewFragment as GuestBasketViewListFragmentType } from '../../../schema/@types/public/GuestBasketListViewFragment';

const SAVED_BASKET_PROSPECT_HASHES_KEY = 'savedBasketProspectHashes';
const GUEST_BASKET_HASH_KEY = 'guestBasketHash';
const PROSPECT_HASH_KEY = 'prospectHash';

/**
 * Retrieves guestBasketHash from local storage if available
 *
 * @returns Object containing guestBasketHash or empty object
 */
export function getGuestBasketHash() {
  let guestBasketHash;

  if (Store.hasStorage) {
    const storage = Store.getStorage(StorageType.LocalStorage);

    guestBasketHash = storage.getItem('guestBasketHash');
  }

  return guestBasketHash === 'null' ? undefined : guestBasketHash;
}
/**
 * Retrieves guestBasketId from local storage if available
 *
 * @returns Object containing guestBasketId or empty object
 */
export function getGuestBasketId() {
  let guestBasketId;

  if (Store.hasStorage) {
    const storage = Store.getStorage(StorageType.LocalStorage);

    guestBasketId = storage.getItem('guestBasketId');
  }

  return guestBasketId === 'null' ? undefined : guestBasketId;
}
/**
 * Remove prospectHash and guestBasketHash from Local storage
 */
export function removeProspectAndBasketHash() {
  if (Store.hasStorage) {
    const storage = Store.getStorage(StorageType.LocalStorage);

    Store.removeItems(storage, [
      PROSPECT_HASH_KEY,
      GUEST_BASKET_HASH_KEY,
      SAVED_BASKET_PROSPECT_HASHES_KEY,
    ]);
  }
}
/**
 * Removes prospectHash from local storage, this clears the quote thats not in basket.
 */
export function removeProspectHashFromCache() {
  if (Store.hasStorage) {
    const storage = Store.getStorage(StorageType.LocalStorage);

    Store.removeItems(storage, ['prospectHash']);
  }
}
/**
 * Handles the updated cache. Removes the prospect that has just been
 * deleted from the basket from the cache
 *
 * @param cache - Current apollo cache
 * @param results - Fetch results
 * @param prospectId - Modified prospect ID
 * @param guestBasketHash - Current basket ID
 */
export function modifyBasketCache(
  cache: DataProxy,
  results: FetchResult<RemoveProspectFromBasket, Record<string, unknown>, Record<string, unknown>>,
  prospectId: string,
  guestBasketHash: string
) {
  const { data } = results;

  if (!data) {
    return;
  }

  const fragmentOptions = {
    fragment: GuestBasketListViewFragment,
    fragmentName: 'GuestBasketListViewFragment',
    id: `GuestBasketList:${guestBasketHash}`,
  };
  const basket = cache.readFragment<GuestBasketViewListFragmentType>({
    ...fragmentOptions,
    variables: {
      guestBasketHash,
    },
  });

  if (!basket) {
    return;
  }

  const removedItem = basket.basketItems?.find(item => item.prospectId === prospectId);

  if (removedItem?.prospectHash) {
    removeSavedBasketProspectHash(removedItem.prospectHash);
  }

  basket.basketItems = basket.basketItems?.filter(item => item.prospectId !== prospectId) || [];
  basket.prospectCount = basket.basketItems?.length || 0;
  saveBasketCount(basket.prospectCount.toString());

  if (basket.prospectCount === 0) {
    removeProspectAndBasketHash();
  }

  cache.writeFragment({
    ...fragmentOptions,
    data: basket,
  });
}
/**
 * Saves prospect count in storage
 *
 * @param count - prospects count in basket
 */
export function saveBasketCount(count: string) {
  Store.getStorage(StorageType.LocalStorage).setItem('prospectCount', count);
}
/**
 * Removes a prospect hash from the web summary's saved basket marker cache.
 *
 * @param prospectHash - Prospect hash to remove
 */
function removeSavedBasketProspectHash(prospectHash: string) {
  if (!Store.hasStorage) {
    return;
  }

  const storage = Store.getStorage(StorageType.LocalStorage);
  const rawValue = storage.getItem(SAVED_BASKET_PROSPECT_HASHES_KEY);

  if (!rawValue) {
    return;
  }

  try {
    const parsedValue = JSON.parse(rawValue);

    if (!Array.isArray(parsedValue)) {
      return;
    }

    const nextValue = parsedValue.filter(hash => hash !== prospectHash);

    storage.setItem(SAVED_BASKET_PROSPECT_HASHES_KEY, JSON.stringify(nextValue));
  } catch {
    Store.removeItems(storage, [SAVED_BASKET_PROSPECT_HASHES_KEY]);
  }
}
